import type {
  LibraryCandidateDetail as APILibraryCandidateDetail,
  LibraryCandidateFilters as APILibraryCandidateFilters,
  LibraryEmailForwardAddressResult as APILibraryEmailForwardAddressResult,
  LibraryCandidateSummary as APILibraryCandidateSummary,
  LibraryCollectionDetail as APILibraryCollectionDetail,
  LibraryCollectionSummary as APILibraryCollectionSummary,
  LibraryListCandidatesResult as APILibraryListCandidatesResult,
  LibraryListCollectionsResult as APILibraryListCollectionsResult,
  LibraryListProjectsResult as APILibraryListProjectsResult,
  LibraryOverviewResult as APILibraryOverviewResult,
  LibraryProjectDetail as APILibraryProjectDetail,
  LibraryProjectPipelineStage,
  LibraryProjectSummary as APILibraryProjectSummary,
  LibraryRoleSummary as APILibraryRoleSummary,
  RoleOverviewResult,
} from '@tabbio/types';
import { api } from './api';
import { getLibraryResponseError } from './library-api-error';
import type {
  LibraryCandidate,
  LibraryCandidateDetail,
  LibraryCandidateKind,
  LibraryCollection,
  LibraryDecision,
  LibraryProject,
  LibraryProjectCandidate,
  LibraryProjectDetail,
  LibraryRole,
  LibrarySort,
  LibraryStage,
  LibrarySource,
} from './library/types';

const FALLBACK_AVATAR_TONE = '#D8DADF';

export interface FetchLibraryCandidatesParams {
  roleId?: string;
  q?: string;
  sort?: LibrarySort;
  stages?: LibraryStage[];
  sources?: LibrarySource[];
  decisions?: LibraryDecision[];
  locations?: string[];
  collectionId?: string;
  collectionIds?: string[];
  candidateKinds?: LibraryCandidateKind[];
  starredOnly?: boolean;
  cursor?: string;
  limit?: number;
}

export interface LibraryOverviewResult {
  counts: {
    allCandidates: number;
    talentPool: number;
    offlineCvs: number;
  };
  roles: LibraryRole[];
  collections: LibraryCollection[];
  setupRequired?: boolean;
}

export interface LibraryListCandidatesResult {
  candidates: LibraryCandidate[];
  nextCursor: string | null;
  total: number;
  searchSessionId?: string | null;
  setupRequired?: boolean;
}

export interface LibraryEmailForwardAddressResult {
  emailAddress: string;
  provider: 'sendgrid';
  configured: boolean;
  readiness: import('./library/types').LibraryEmailForwardingReadiness;
}

export interface UpdateLibraryRoleInput {
  name?: string;
}

export interface CreateLibraryCollectionInput {
  name: string;
  description?: string;
}

export interface UpdateLibraryCollectionInput {
  name?: string;
  description?: string;
}

function mapRole(role: APILibraryRoleSummary): LibraryRole {
  return {
    id: role.id,
    name: role.name,
    status: role.status,
    createdAt: role.createdAt,
    candidateCount: role.candidateCount,
  };
}

function mapCollection(
  collection: APILibraryCollectionSummary | APILibraryCollectionDetail,
): LibraryCollection {
  return {
    id: collection.id,
    name: collection.name,
    description: collection.description,
    candidateCount: collection.candidateCount,
    createdAt: collection.createdAt,
    updatedAt: collection.updatedAt,
    ...(Array.isArray((collection as APILibraryCollectionDetail).candidateIds)
      ? { candidateIds: (collection as APILibraryCollectionDetail).candidateIds }
      : {}),
  };
}

function mapCandidate(candidate: APILibraryCandidateSummary): LibraryCandidate {
  return {
    id: candidate.id,
    userId: candidate.userId,
    roleId: candidate.primaryRoleId,
    roleIds: candidate.roleIds,
    name: candidate.name,
    title: candidate.title,
    location: candidate.location || '',
    initials: candidate.initials,
    avatarTone: candidate.avatarTone || FALLBACK_AVATAR_TONE,
    matchScore: candidate.matchScore,
    stage: candidate.stage,
    source: candidate.source,
    decision: candidate.decision,
    starred: candidate.starred,
    skills: candidate.skills,
    appliedLabel: candidate.appliedLabel,
    createdAt: candidate.createdAt,
    updatedAt: candidate.updatedAt,
    kind: candidate.kind,
    parseStatus: candidate.parseStatus ?? 'ready',
    collectionIds: candidate.collectionIds,
    cvSnippet: candidate.cvSnippet,
  };
}

function mapCandidateDetail(candidate: APILibraryCandidateDetail): LibraryCandidateDetail {
  return {
    ...mapCandidate(candidate),
    roles: candidate.roles.map(mapRole),
    collections: candidate.collections.map(mapCollection),
    experiences: candidate.experiences,
    education: candidate.education,
    summary: candidate.summary,
    documents: candidate.documents.map((document) => ({
      id: document.id,
      fileName: document.fileName,
      mimeType: document.mimeType,
      fileUrl: document.fileUrl,
      sourceText: document.sourceText,
      createdAt: document.createdAt,
    })),
  };
}

function appendArrayParam(params: URLSearchParams, key: string, values?: string[]) {
  if (!values?.length) return;
  params.set(key, values.join(','));
}

export async function fetchRoleOverview(roleId: string): Promise<RoleOverviewResult> {
  const response = await api.get<RoleOverviewResult>(
    `/api/library/roles/${encodeURIComponent(roleId)}/overview`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  return (
    response.data ?? {
      applicantCount: 0,
      savedCount: 0,
      shortlistedCount: 0,
      contactedCount: 0,
      highMatchNotContactedCount: 0,
      lastApplicantAt: null,
      savedSearchCount: 0,
    }
  );
}

export async function fetchLibraryOverview(): Promise<LibraryOverviewResult> {
  const response = await api.get<APILibraryOverviewResult>('/api/library/overview');
  const error = getLibraryResponseError(response);
  if (error) throw error;

  const data = response.data;
  if (!data) {
    return {
      counts: { allCandidates: 0, talentPool: 0, offlineCvs: 0 },
      roles: [],
      collections: [],
      setupRequired: Boolean(response.meta?.setupRequired),
    };
  }

  return {
    counts: data.counts,
    roles: data.roles.map(mapRole),
    collections: data.collections.map(mapCollection),
    setupRequired: Boolean(response.meta?.setupRequired),
  };
}

export async function fetchLibraryRoles(): Promise<LibraryRole[]> {
  const response = await api.get<APILibraryRoleSummary[]>('/api/library/roles');
  const error = getLibraryResponseError(response);
  if (error) throw error;
  return (response.data ?? []).map(mapRole);
}

export async function fetchLibraryCandidates(
  input: FetchLibraryCandidatesParams = {},
): Promise<LibraryListCandidatesResult> {
  const params = new URLSearchParams();

  if (input.roleId) params.set('roleId', input.roleId);
  if (input.q?.trim()) params.set('q', input.q.trim());
  if (input.sort) params.set('sort', input.sort);
  if (input.cursor) params.set('cursor', input.cursor);
  if (input.limit) params.set('limit', String(input.limit));
  appendArrayParam(params, 'stages', input.stages);
  appendArrayParam(params, 'sources', input.sources);
  appendArrayParam(params, 'decisions', input.decisions);
  appendArrayParam(params, 'locations', input.locations);
  appendArrayParam(
    params,
    'collectionIds',
    input.collectionIds?.length
      ? input.collectionIds
      : input.collectionId
        ? [input.collectionId]
        : undefined,
  );
  appendArrayParam(params, 'candidateKinds', input.candidateKinds);
  if (input.starredOnly) params.set('starredOnly', 'true');

  const response = await api.get<APILibraryListCandidatesResult>(
    `/api/library/candidates${params.size ? `?${params.toString()}` : ''}`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;

  return {
    candidates: (response.data?.candidates ?? []).map(mapCandidate),
    nextCursor: response.data?.nextCursor ?? null,
    total: response.data?.total ?? 0,
    searchSessionId: response.data?.searchSessionId ?? null,
    setupRequired: Boolean(response.meta?.setupRequired),
  };
}

export async function fetchLibraryCandidateDetail(
  candidateId: string,
  searchSessionId?: string | null,
): Promise<LibraryCandidateDetail> {
  const params = new URLSearchParams();
  if (searchSessionId) {
    params.set('searchSessionId', searchSessionId);
  }
  const response = await api.get<APILibraryCandidateDetail>(
    `/api/library/candidates/${candidateId}${params.size ? `?${params.toString()}` : ''}`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) {
    throw new Error('Candidate not found');
  }
  return mapCandidateDetail(response.data);
}

export async function updateLibraryRole(
  roleId: string,
  input: UpdateLibraryRoleInput,
): Promise<LibraryRole> {
  const response = await api.patch<APILibraryRoleSummary>(
    `/api/library/roles/${roleId}`,
    input,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Role update returned no data');
  return mapRole(response.data);
}

export async function toggleLibraryRoleStatus(roleId: string): Promise<LibraryRole> {
  const response = await api.post<APILibraryRoleSummary>(
    `/api/library/roles/${roleId}/toggle-status`,
    {},
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Role toggle returned no data');
  return mapRole(response.data);
}

export async function deleteLibraryRole(roleId: string): Promise<void> {
  const response = await api.delete(`/api/library/roles/${roleId}`);
  const error = getLibraryResponseError(response);
  if (error) throw error;
}

export async function rejectLibraryCandidate(
  candidateId: string,
): Promise<LibraryCandidateDetail> {
  const response = await api.post<APILibraryCandidateDetail>(
    `/api/library/candidates/${candidateId}/reject`,
    {},
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Candidate update returned no data');
  return mapCandidateDetail(response.data);
}

export async function starLibraryCandidate(
  candidateId: string,
): Promise<LibraryCandidateDetail> {
  const response = await api.post<APILibraryCandidateDetail>(
    `/api/library/candidates/${candidateId}/star`,
    {},
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Candidate update returned no data');
  return mapCandidateDetail(response.data);
}

export async function unstarLibraryCandidate(
  candidateId: string,
): Promise<LibraryCandidateDetail> {
  const response = await api.delete<APILibraryCandidateDetail>(
    `/api/library/candidates/${candidateId}/star`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Candidate update returned no data');
  return mapCandidateDetail(response.data);
}

export async function fetchLibraryCollections(): Promise<LibraryCollection[]> {
  const response = await api.get<APILibraryListCollectionsResult>('/api/library/collections');
  const error = getLibraryResponseError(response);
  if (error) throw error;
  return (response.data?.collections ?? []).map(mapCollection);
}

export async function fetchLibraryCollectionDetail(
  collectionId: string,
): Promise<LibraryCollection> {
  const response = await api.get<APILibraryCollectionDetail>(`/api/library/collections/${collectionId}`);
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Collection not found');
  return mapCollection(response.data);
}

export async function createLibraryCollection(
  input: CreateLibraryCollectionInput,
): Promise<LibraryCollection> {
  const response = await api.post<APILibraryCollectionSummary>(
    '/api/library/collections',
    input,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Collection creation returned no data');
  return mapCollection(response.data);
}

export async function updateLibraryCollection(
  collectionId: string,
  input: UpdateLibraryCollectionInput,
): Promise<LibraryCollection> {
  const response = await api.patch<APILibraryCollectionSummary>(
    `/api/library/collections/${collectionId}`,
    input,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Collection update returned no data');
  return mapCollection(response.data);
}

export async function deleteLibraryCollection(collectionId: string): Promise<void> {
  const response = await api.delete(`/api/library/collections/${collectionId}`);
  const error = getLibraryResponseError(response);
  if (error) throw error;
}

export async function setLibraryCollectionCandidates(
  collectionId: string,
  candidateIds: string[],
): Promise<LibraryCollection> {
  const response = await api.put<APILibraryCollectionDetail>(
    `/api/library/collections/${collectionId}/candidates`,
    { candidateIds },
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Collection update returned no data');
  return mapCollection(response.data);
}

export async function uploadLibraryCandidate(
  formData: FormData,
): Promise<LibraryCandidateDetail> {
  const json = await api.postForm<APILibraryCandidateDetail>(
    '/api/library/ingest/upload',
    formData,
  );
  const error = getLibraryResponseError(json);
  if (error) throw error;
  if (!json.data) throw new Error('Candidate upload returned no data');
  return mapCandidateDetail(json.data);
}

export async function fetchLibraryEmailForwardAddress(): Promise<LibraryEmailForwardAddressResult> {
  const response = await api.get<APILibraryEmailForwardAddressResult>(
    '/api/library/ingest/email-address',
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) {
    throw new Error('Forwarding address not available');
  }

  return response.data;
}

export function toLibraryCandidateFilters(
  input: FetchLibraryCandidatesParams,
): APILibraryCandidateFilters {
  return {
    ...input,
    decisions: input.decisions,
    collectionIds: input.collectionIds,
    kinds: input.candidateKinds,
  };
}

function mapProject(record: APILibraryProjectSummary): LibraryProject {
  return {
    id: record.id,
    companyId: record.companyId,
    name: record.name,
    description: record.description,
    status: record.status,
    jobId: record.jobId,
    jobTitle: record.jobTitle,
    stageCounts: record.stageCounts,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
  };
}

function mapProjectDetail(record: APILibraryProjectDetail): LibraryProjectDetail {
  return {
    ...mapProject(record),
    candidates: record.candidates.map<LibraryProjectCandidate>((item) => ({
      id: item.id,
      projectId: item.projectId,
      candidateId: item.candidateId,
      pipelineStage: item.pipelineStage,
      sortOrder: item.sortOrder,
      addedAt: item.addedAt,
      updatedAt: item.updatedAt,
      candidate: mapCandidate(item.candidate),
    })),
  };
}

export interface CreateLibraryProjectInput {
  name: string;
  description?: string;
  jobId?: string | null;
}

export interface UpdateLibraryProjectInput {
  name?: string;
  description?: string;
  status?: 'active' | 'archived';
  jobId?: string | null;
}

export async function fetchLibraryProjects(): Promise<LibraryProject[]> {
  const response = await api.get<APILibraryListProjectsResult>(
    '/api/library/projects',
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  return (response.data?.projects ?? []).map(mapProject);
}

export async function fetchLibraryProject(
  projectId: string,
): Promise<LibraryProjectDetail> {
  const response = await api.get<APILibraryProjectDetail>(
    `/api/library/projects/${encodeURIComponent(projectId)}`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Project not found');
  return mapProjectDetail(response.data);
}

export async function createLibraryProject(
  input: CreateLibraryProjectInput,
): Promise<LibraryProject> {
  const response = await api.post<APILibraryProjectSummary>(
    '/api/library/projects',
    input,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Project create returned no data');
  return mapProject(response.data);
}

export async function updateLibraryProject(
  projectId: string,
  input: UpdateLibraryProjectInput,
): Promise<LibraryProject> {
  const response = await api.patch<APILibraryProjectSummary>(
    `/api/library/projects/${encodeURIComponent(projectId)}`,
    input,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Project update returned no data');
  return mapProject(response.data);
}

export async function deleteLibraryProject(projectId: string): Promise<void> {
  const response = await api.delete(
    `/api/library/projects/${encodeURIComponent(projectId)}`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
}

export async function attachLibraryProjectCandidates(
  projectId: string,
  candidateIds: string[],
  pipelineStage?: LibraryProjectPipelineStage,
): Promise<LibraryProjectDetail> {
  const response = await api.post<APILibraryProjectDetail>(
    `/api/library/projects/${encodeURIComponent(projectId)}/candidates`,
    { candidateIds, ...(pipelineStage ? { pipelineStage } : {}) },
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Project attach returned no data');
  return mapProjectDetail(response.data);
}

export async function detachLibraryProjectCandidate(
  projectId: string,
  candidateId: string,
): Promise<void> {
  const response = await api.delete(
    `/api/library/projects/${encodeURIComponent(projectId)}/candidates/${encodeURIComponent(candidateId)}`,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
}

export async function moveLibraryProjectCandidateStage(
  projectId: string,
  candidateId: string,
  pipelineStage: LibraryProjectPipelineStage,
): Promise<LibraryProjectDetail> {
  const response = await api.post<APILibraryProjectDetail>(
    `/api/library/projects/${encodeURIComponent(projectId)}/candidates/${encodeURIComponent(candidateId)}/stage`,
    { pipelineStage },
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Project stage move returned no data');
  return mapProjectDetail(response.data);
}
